import Cookies from 'universal-cookie';
import { Pagination } from '~/Api/Pagination';
import { ExchangeRates, Filter, UserProductGroup } from '~/Api/User/ProductGroup';
import config from '~/config';
import { Action as ExchangeRateAction, ActionType as ExchangeRateActionType } from '~/ExchangeRate';
import RecentUserProductGroupsCookieStorage from '~/libs/UserProductGroup/RecentUserProductGroupsCookieStorage';

import { Action, ActionType } from './Action';
import ProductGroupActionTypes from './ActionTypes';
import GroupProductActionTypes from '~/GroupProduct/ActionTypes';
import { Action as GroupProductAction } from '~/GroupProduct/reducer';

interface GroupState {
	request: Promise<any> | null;
	status: 'fetching' | 'fetched' | 'fetched-groups' | 'not-found';
	group: UserProductGroup | null;
}

interface GroupsState {
	request: Promise<any> | null;
	status: 'fetching' | 'fetched';
	pagination: Pagination | null;
	ids: string[] | null;
}

export interface State {
	byId: { [key in string]: GroupState };
	byFilter: { [key in string]: GroupsState };
	recentIds: { request: Promise<any> | null; ids: string[] } | null;
	recentStorage: RecentUserProductGroupsCookieStorage;
	createRequest: Promise<any> | null;
	removeRequest: Promise<any> | null;
	updateRequests: { [key in string]: Promise<any> };
	exportRequest: Promise<any> | null;
}

export interface MappedGroupsState {
	status: 'not-fetched' | 'fetching' | 'fetched';
	pagination: Pagination | null;
	groups: UserProductGroup[];
}

type OldAction =
	| {
			type: ProductGroupActionTypes.requestGroups;
			request: Promise<any>;
			filter: Filter;
	  }
	| {
			type: ProductGroupActionTypes.receiveGroups;
			request: Promise<any>;
			filter: Filter;
			groups: UserProductGroup[] | null;
			pagination: Pagination;
	  }
	| { type: ProductGroupActionTypes.requestGroup; request: Promise<any>; id: string }
	| {
			type: ProductGroupActionTypes.receiveGroup;
			request: Promise<any>;
			id: string;
			group: UserProductGroup | null;
			notFound: boolean;
	  }
	| { type: ProductGroupActionTypes.prune }
	| { type: ProductGroupActionTypes.requestRecent; request: Promise<any> }
	| {
			type: ProductGroupActionTypes.receiveRecent;
			request: Promise<any>;
			groups: UserProductGroup[];
	  }
	| { type: ProductGroupActionTypes.requestCreate; request: Promise<any> }
	| {
			type: ProductGroupActionTypes.receiveCreate;
			request: Promise<any>;
			group: UserProductGroup | null;
	  }
	| { type: ProductGroupActionTypes.requestUpdate; request: Promise<any>; id: string }
	| {
			type: ProductGroupActionTypes.receiveUpdate;
			request: Promise<any>;
			id: string;
			group: UserProductGroup | null;
	  }
	| { type: ProductGroupActionTypes.requestRemove; request: Promise<any> }
	| {
			type: ProductGroupActionTypes.receiveRemove;
			request: Promise<any>;
			id: string | null;
	  };

function createState(): State {
	return {
		byId: {},
		byFilter: {},
		recentIds: null,
		recentStorage: new RecentUserProductGroupsCookieStorage(
			new Cookies(),
			config.userProductGroup.maxRecentGroupsCount,
			'recent-groups',
			{
				path: '/',
				httpOnly: false,
			}
		),
		createRequest: null,
		removeRequest: null,
		updateRequests: {},
		exportRequest: null,
	};
}

export default function reducer(
	state: State = createState(),
	action: Action | OldAction | ExchangeRateAction | GroupProductAction
) {
	switch (action.type) {
		case ProductGroupActionTypes.requestGroups:
			return {
				...state,
				byFilter: {
					...state.byFilter,
					[action.filter.key]: {
						...(state.byFilter[action.filter.key] || {
							pagination: null,
							ids: null,
						}),
						request: action.request,
						status: 'fetching',
					},
				},
			};

		case ProductGroupActionTypes.receiveGroups: {
			const groupsState = state.byFilter[action.filter.key];
			if (!groupsState || groupsState.request !== action.request) {
				return state;
			}

			if (!action.groups) {
				const byFilter = { ...state.byFilter };
				delete byFilter[action.filter.key];
				return { ...state, byFilter };
			}

			const { filter, pagination, groups } = action;
			const byId = { ...state.byId };
			const ids = groupsState.ids ? [...groupsState.ids] : [];

			ids.splice(
				action.pagination.getFirstIndex(),
				pagination.perPage,
				...groups.map((group) => {
					if (!byId[group.id]) {
						byId[group.id] = { request: null, status: 'fetched-groups', group };
					}
					return group.id;
				})
			);

			return {
				...state,
				byId,
				byFilter: {
					...state.byFilter,
					[filter.key]: {
						request: null,
						status: 'fetched',
						pagination,
						ids,
					},
				},
			};
		}

		case ProductGroupActionTypes.requestGroup:
			return {
				...state,
				byId: {
					...state.byId,
					[action.id]: {
						...(state.byId[action.id] || { group: null }),
						request: action.request,
						status: 'fetching',
					},
				},
			};

		case ProductGroupActionTypes.receiveGroup: {
			const groupState = state.byId[action.id];
			if (!groupState || groupState.request !== action.request) {
				return state;
			}

			return {
				...state,
				byId: {
					...state.byId,
					[action.id]: {
						request: null,
						status: action.group ? 'fetched' : action.notFound ? 'not-found' : 'failed',
						group: action.group,
					},
				},
			};
		}

		case ProductGroupActionTypes.requestRecent:
			return {
				...state,
				recentIds: { request: action.request, ids: [] },
			};

		case ProductGroupActionTypes.receiveRecent: {
			if (state.recentIds && state.recentIds.request !== action.request) {
				return state;
			}

			const byId = { ...state.byId };
			const ids = action.groups.map((group) => {
				if (!byId[group.id]) {
					byId[group.id] = { request: null, status: 'fetched-groups', group };
				}
				return group.id;
			});

			return {
				...state,
				byId,
				recentIds: { request: null, ids },
			};
		}

		case ProductGroupActionTypes.prune:
			return createState();

		case ProductGroupActionTypes.requestCreate:
			return { ...state, createRequest: action.request };

		case ProductGroupActionTypes.receiveCreate: {
			if (state.createRequest !== action.request) {
				return state;
			}
			if (!action.group) {
				return { ...state, createRequest: null };
			}

			const parentId = action.group.parent ? action.group.parent.id : null;
			const parentState = parentId ? state.byId[parentId] : null;

			const filter = new Filter(parentId, null);
			const groupsState = state.byFilter[filter.key];
			const byId = {
				...state.byId,
				[action.group.id]: {
					request: null,
					status: 'fetched-groups',
					group: action.group,
				},
			};

			if (parentState && parentState.group) {
				byId[parentId || ''] = {
					...parentState,
					group: parentState.group.productGroupsCountAndItemsUpdatedTimeUpdated(
						parentState.group.productGroupsCount + 1,
						new Date()
					),
				};
			}

			return {
				...state,
				byId,
				byFilter:
					groupsState && groupsState.ids
						? {
								...state.byFilter,
								[filter.key]: {
									...groupsState,
									ids: [action.group.id, ...groupsState.ids],
								},
						  }
						: state.byFilter,
				createRequest: null,
			};
		}

		case ProductGroupActionTypes.requestUpdate:
		case ActionType.requestHistoricDataViewSettingsUpdate:
			return {
				...state,
				updateRequests: {
					...state.updateRequests,
					[action.id]: action.request,
				},
			};

		case ProductGroupActionTypes.receiveUpdate: {
			if (state.updateRequests[action.id] !== action.request) {
				return state;
			}
			const groupState = action.group ? state.byId[action.group.id] : null;
			const updateRequests = { ...state.updateRequests };
			delete updateRequests[action.id];

			return {
				...state,
				byId: action.group
					? {
							...state.byId,
							[action.group.id]: {
								request: null,
								status: groupState ? groupState.status : 'fetched-groups',
								group: action.group,
							},
					  }
					: state.byId,
				updateRequests,
			};
		}

		case ActionType.receiveHistoricDataViewSettingsUpdate:
			if (state.updateRequests[action.id] !== action.request) {
				return state;
			}
			const updateRequests = { ...state.updateRequests };
			delete updateRequests[action.id];

			const groupState = state.byId[action.id];

			return {
				...state,
				byId:
					groupState !== undefined && action.historicDataViewSettings !== null
						? {
								...state.byId,
								[action.id]: {
									request: null,
									status: groupState ? groupState.status : 'fetched-groups',
									group: groupState?.group?.historicProductDataViewDateUpdated(
										action.historicDataViewSettings.historicProductDataViewDate
									),
								},
						  }
						: state.byId,
				updateRequests,
			};

		case ProductGroupActionTypes.requestRemove:
			return { ...state, removeRequest: action.request };

		case ProductGroupActionTypes.receiveRemove: {
			if (state.removeRequest !== action.request) {
				return state;
			}
			if (!action.id) {
				return { ...state, removeRequest: null };
			}

			const groupState = state.byId[action.id];
			const parentId = groupState && groupState.group && groupState.group.parent ? groupState.group.parent.id : null;
			const parentState = parentId ? state.byId[parentId] : null;

			const byId = { ...state.byId };
			delete byId[action.id];

			if (parentState && parentState.group) {
				byId[parentId || ''] = {
					...parentState,
					group: parentState.group.productGroupsCountAndItemsUpdatedTimeUpdated(
						parentState.group.productGroupsCount - 1,
						new Date()
					),
				};
			}

			return {
				...state,
				byId,
				removeRequest: null,
			};
		}

		case ActionType.requestExport:
			return { ...state, exportRequest: action.request };

		case ActionType.receiveExport:
			if (state.exportRequest !== action.request) {
				return state;
			}
			return { ...state, exportRequest: null };

		case ExchangeRateActionType.receiveUpdateGroupRates: {
			const groupState = state.byId[action.groupId];
			if (!action.settings || !groupState || !groupState.group) {
				return state;
			}

			return {
				...state,
				byId: {
					...state.byId,
					[action.groupId]: {
						...groupState,
						group: groupState.group.exchangeRatesUpdated(
							new ExchangeRates(action.settings, groupState.group.exchangeRates.availableCurrencies)
						),
					},
				},
			};
		}

		case GroupProductActionTypes.receiveRemoveFromGroup:
		case GroupProductActionTypes.receiveAddToGroup: {
			const groupId = action.groupId || action.group?.id;
			if (!groupId) {
				return state;
			}

			const groupState = state.byId[groupId];
			if (!groupState || !groupState.group) {
				return state;
			}

			return {
				...state,
				byId: {
					...state.byId,
					[groupId]: {
						...groupState,
						group: groupState.group.productsCountAndItemsUpdatedTimeUpdated(
							groupState.group.productsCount +
								(action.type === GroupProductActionTypes.receiveRemoveFromGroup ? -1 : 1),
							new Date()
						),
					},
				},
			};
		}

		default:
			return state;
	}
}

export function selectGroup(state: State, id: string | null): GroupState | null {
	if (!id) {
		return null;
	}
	return state.byId[id] && state.byId[id].status !== 'fetched-groups' ? state.byId[id] : null;
}

export function selectGroups(state: State, filter: Filter): MappedGroupsState {
	const groupsState = state.byFilter[filter.key];
	if (!groupsState) {
		return { status: 'not-fetched', pagination: null, groups: [] };
	}
	if (!groupsState.ids) {
		return { status: groupsState.status, pagination: null, groups: [] };
	}

	return {
		status: groupsState.status,
		pagination: groupsState.pagination,
		groups: selectGroupsByIds(state, groupsState.ids),
	};
}

export function selectRecent(state: State): UserProductGroup[] {
	return state.recentIds ? selectGroupsByIds(state, state.recentIds.ids) : [];
}

function selectGroupsByIds(state: State, ids: string[]): UserProductGroup[] {
	return ids.reduce((userGroups: UserProductGroup[], id) => {
		const groupState = state.byId[id];

		if (groupState?.group) {
			return userGroups.concat([groupState.group]);
		}
		return userGroups;
	}, []);
}

export function selectUpdate(state: State, id: string | null): Promise<any> | null {
	if (!id) {
		return null;
	}

	return state.updateRequests[id] || null;
}
